import React, { useMemo } from "react";
import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"
import InputWithAddOptions from "@components/input/InputWithAddOptions"
import { useReduxSelector } from "@hooks/useReduxSelector"
import { checkEnterpriseId } from "@helpers/_functions"
import useHealth from "@pages/shared/employeFormNew/PersonnalInformations/hooksHealth"

export const useSectionHealthColumns = () => {
  const { t } = useTranslation("pages.employés.healthPrecision")

  return useMemo(() => [
    { id: "allergies", label: t("Allergies") },
    { id: "foodPreferences", label: t("Préférences alimentaires") }
  ], [t])
}

export const SectionHealthTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.healthPrecision")

  return (
    <>
      <th
        key="allergies"
        className={`arh-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.allergies ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Allergies")}
      </th>
      <th
        key="foodPreferences"
        className={`arh-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.foodPreferences ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Préférences alimentaires")}
      </th>
    </>
  )
}

export const SectionHealthTableBulkCells = ({ control, employeeIndex, employee, setSuperZIndex, readOnly = false, mustWait, hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.healthPrecision")
  const { deleteHealth, upsertOption } = useHealth()
  const allergies = useReduxSelector(({ enterpriseEmployeData: { allergies } }) => allergies?.list)
  const foodPreferences = useReduxSelector(({ enterpriseEmployeData: { foods } }) => foods?.list)

  const readOnlyAccess = mustWait || readOnly

  return (
    <>
      <td
        key="allergies"
        className={`arh-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.allergies ? "arh-hidden" : ""}`}
      >
        <div className="arh-flex arh-flex-col">
          <Controller
            control={control}
            name={`employees.${employeeIndex}.allergies`}
            defaultValue={employee?.allergies || []}
            render={({ field: { onChange, value, name }, fieldState: { invalid } }) => (
              <InputWithAddOptions
                name={name}
                defaultText={mainTranslator("Allergies")}
                optionsList={allergies}
                secondDefaultText={mainTranslator("Ajouter")}
                value={value}
                optionListLabel="name"
                onChange={onChange}
                addItem={(body) =>
                  upsertOption(
                    {
                      ...body,
                      type: "allergy",
                      Enterprise: checkEnterpriseId(),
                    },
                    "allergy",
                  )
                }
                editItem={(body) =>
                  upsertOption(
                    {
                      ...body,
                      type: "allergy",
                      Enterprise: checkEnterpriseId(),
                    },
                    "allergy",
                  )
                }
                deleteItem={(body) => deleteHealth(body, "allergy")}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                validateDropdown={invalid}
                multiple
                setSuperZIndex={setSuperZIndex}
                showAction
                disabled={readOnlyAccess}
                classBtn="arh-absolute arh-right-1 arh-top-1/2 -arh-translate-y-1/2 !arh-py-[0.0625rem] !arh-px-2.5"
              />
            )}
          />
        </div>
      </td>
      <td
        key="foodPreferences"
        className={`arh-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.foodPreferences ? "arh-hidden" : ""}`}
      >
        <div className="arh-flex arh-flex-col">
          <Controller
            control={control}
            name={`employees.${employeeIndex}.foodPreferences`}
            defaultValue={employee?.foodPreferences || []}
            render={({ field: { onChange, value, name }, fieldState: { invalid } }) => (
              <InputWithAddOptions
                name={name}
                defaultText={mainTranslator("Préférences alimentaires")}
                optionsList={foodPreferences}
                secondDefaultText={mainTranslator("Ajouter")}
                value={value}
                optionListLabel="name"
                onChange={onChange}
                addItem={(body) =>
                  upsertOption(
                    {
                      ...body,
                      type: "foodPreference",
                      Enterprise: checkEnterpriseId(),
                    },
                    "preferenceFood",
                  )
                }
                editItem={(body) =>
                  upsertOption(
                    {
                      ...body,
                      type: "foodPreference",
                      Enterprise: checkEnterpriseId(),
                    },
                    "preferenceFood",
                  )
                }
                deleteItem={(body) => deleteHealth(body, "preferenceFood")}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                validateDropdown={invalid}
                multiple
                setSuperZIndex={setSuperZIndex}
                showAction
                disabled={readOnlyAccess}
                classBtn="arh-absolute arh-right-1 arh-top-1/2 -arh-translate-y-1/2 !arh-py-[0.0625rem] !arh-px-2.5"
              />
            )}
          />
        </div>
      </td>
    </>
  )
}

export const getHealthColumnCount = () => 2